import { createTelemetryObject, createTelemetry } from '@shared/core/analytics';
import type { SortOption } from '@shared/constants/accommodations';
import type { PriceSource } from '@shared/constants/accommodations';
import type { Maybe, TravelOfferProvider } from '@graphql/generated-base';
import type { FeaturedPlacementVariant } from '../../hooks/useFeaturedPlacements.types';
import type { AccommodationsScenario } from '@shared/types/Accommodations.types';
import type { FeaturedPlacementTelemetryFields } from '../../FeaturedPlacementTelemetryContext';
import type { AffiliateHandoffKind } from '@shared/utils/formatting/affiliateHandoff.types';

export const listingsPanelTelemetry = createTelemetryObject({
  actions: {
    /**
     * A heart on a listing tile. `saved` false is an unheart; `savedBy` is who has it afterwards.
     * `boardId`/`createdBoard`: the board chosen in "Save to…", and whether it was made for this save.
     * `scenario`: which console the heart was on.
     */
    tripBoardSaveToggled: ({
      joyPlaceId,
      hotelName,
      tripType,
      saved,
      savedBy,
      boardId,
      createdBoard,
      scenario,
    }: {
      joyPlaceId: string;
      hotelName?: string;
      tripType: string;
      saved: boolean;
      savedBy: string;
      boardId?: string;
      createdBoard?: boolean;
      scenario?: AccommodationsScenario;
    }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'tripBoardSaveToggled',
        joyPlaceId,
        hotelName,
        from: 'Accommodations Console',
        trip_type: tripType,
        saved,
        saved_by: savedBy,
        ...(boardId && { board_id: boardId, created_board: createdBoard ?? false }),
        ...(scenario && { scenario }),
      },
    }),
    /**
     * Fires when a guest opens a hotel from a listing tile, a map marker popup or the tile CTA.
     *
     * `travelOfferProvider` names the provider whose price the tile displayed. `priceSource` names
     * the fetch path that produced it. The tile can show a snapshot price from one provider. The
     * detail page can then show a live price from a different provider. This happens because
     * travel_rate_snapshot holds one row per joyPlace, regardless of provider. Together the two
     * attributes make that switch measurable across a click.
     *
     * `featuredPlacementVariant` and `scenario` are additive (TRAV, featured-placement A/B test) and
     * match the keys and types `destinationSearched` already reports — no existing field changed
     * name or meaning, `isSponsored` least of all. See the doc comment on each below for why a click
     * cannot be attributed to an arm without them.
     */
    seeAvailabilityClicked: ({
      joyPlaceId,
      hotelName,
      isSponsored,
      isDirectBooking,
      chain,
      isCoupleFavourite,
      priceSource,
      travelOfferProvider,
      featuredPlacementVariant,
      scenario,
      placementPosition,
      otaNightlyBaseRate,
      otaTotalRate,
      otaCurrency,
      otaPriceSource,
      name = 'seeAvailabilityClicked',
    }: {
      joyPlaceId?: string;
      hotelName?: string;
      isSponsored?: boolean;
      /**
       * The brand's own direct-booking link replaced the redirect, so the tile showed no rate and
       * the click leaves for the brand's site. A superset of `isSponsored`: every paid ad slot is
       * also a direct-booking handoff, but most direct-booking tiles won no slot. Read the two
       * together to separate "we were paid for this" from "we handed the booking over".
       */
      isDirectBooking?: boolean;
      chain?: string | null;
      isCoupleFavourite?: boolean;
      /** Undefined when the tile showed no provider price — a room block, or a price still loading. */
      priceSource?: PriceSource;
      /** Undefined when the redirect books no specific rate, such as a sold-out hotel search fallback. */
      travelOfferProvider?: TravelOfferProvider;
      /**
       * Joy-side OTA nightly/total we would have charged. On a direct-booking click this is the
       * transferred-GBV figure, not the brand's public rate. Undefined when we had no OTA rate.
       */
      otaNightlyBaseRate?: number;
      otaTotalRate?: number;
      otaCurrency?: string;
      otaPriceSource?: PriceSource;
      /**
       * Which arm this searcher is in. Amplitude assigns the variant at EXPOSURE, so clicks can
       * already be segmented by arm through the experiment join — reporting it on the event makes it
       * self-describing instead, so the arm survives a raw export or a warehouse copy that carries no
       * join. Undefined for the scenarios outside the experiment; `'control'` means bucketed into the
       * baseline arm, and the two are not interchangeable.
       */
      featuredPlacementVariant?: FeaturedPlacementVariant;
      /** Which console surface clicked, so the five consoles are not pooled into one number. */
      scenario?: AccommodationsScenario;
      /**
       * 1-based position inside the featured unit; undefined for an ordinary tile. Pairs with the
       * same field on `promotedHotelImpression` to give a per-position click-through rate, which is
       * the only way to see whether the row's later cards are ever reached or whether the arm is
       * really just card one.
       */
      placementPosition?: number;
      name?: string;
    }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name,
        joyPlaceId,
        from: 'Accommodations Console',
        hotelName,
        isSponsored,
        isDirectBooking,
        chain,
        isCoupleFavourite,
        priceSource,
        travelOfferProvider,
        featuredPlacementVariant,
        scenario,
        placementPosition,
        otaNightlyBaseRate,
        otaTotalRate,
        otaCurrency,
        otaPriceSource,
      },
    }),
    /**
     * Fires alongside `seeAvailabilityClicked` when the click actually opens a partner URL.
     *
     * `featuredPlacementVariant` and `scenario` are additive (TRAV, featured-placement A/B test),
     * same keys and types as on `seeAvailabilityClicked` and `destinationSearched`. They exist for
     * the same two reasons: the arm is carried on the event rather than left to Amplitude's
     * experiment join, and `scenario` keeps the five consoles from pooling into one number.
     */
    hotelBookingRedirect: ({
      joyPlaceId,
      hotelName,
      destinationURL,
      destinationDomain,
      destinationName,
      from = 'Accommodations Console',
      name = 'hotelBookingRedirect',
      isSponsored,
      isDirectBooking,
      chain,
      isCoupleFavourite,
      utm_source,
      utm_medium,
      utm_campaign,
      utm_content,
      featuredPlacementVariant,
      scenario,
      placementPosition,
      affiliateHandoff,
      advertiserDomain,
      otaNightlyBaseRate,
      otaTotalRate,
      otaCurrency,
      otaPriceSource,
    }: {
      joyPlaceId?: string;
      hotelName?: string;
      destinationURL: string;
      /**
       * Host of the link we render. For a sponsored card this is always a Joy host, because the URL
       * is wrapped into the redirect service — see `advertiserDomain` for where the guest ends up.
       */
      destinationDomain: string;
      destinationName: string;
      from?: string;
      name?: string;
      isSponsored?: boolean;
      /** Same meaning as on the click event above. */
      isDirectBooking?: boolean;
      chain?: string | null;
      isCoupleFavourite?: boolean;
      utm_source?: string;
      utm_medium?: string;
      utm_campaign?: string;
      utm_content?: string;
      /** Undefined outside the experiment scenarios; `'control'` is the in-experiment baseline arm. */
      featuredPlacementVariant?: FeaturedPlacementVariant;
      /** The surface that redirected, so the five consoles are not pooled into one number. */
      scenario?: AccommodationsScenario;
      /** 1-based position inside the featured unit; undefined for an ordinary tile. */
      placementPosition?: number;
      /**
       * Whether the affiliate/monetization hop is live on this handoff.
       *
       * This is the measurable half of "direct-booking handoffs always carry the affiliate link": a
       * direct-booking click reporting anything other than `'wrapped'` is one we cannot attribute,
       * and is a defect rather than a segment. Read it together with `isDirectBooking`.
       *
       * It does NOT carry the affiliate URL itself — the Partnerize rewrite happens server-side in
       * app-redirect at click time, so the browser never sees it.
       */
      affiliateHandoff?: AffiliateHandoffKind;
      /** Where the guest actually lands (`marriott.com`), as opposed to the Joy wrap host. */
      advertiserDomain?: string | null;
      /** Joy-side OTA rate we would have charged. Not the brand's public rate. */
      otaNightlyBaseRate?: number;
      otaTotalRate?: number;
      otaCurrency?: string;
      otaPriceSource?: PriceSource;
    }) => ({
      category: 'travel',
      action: 'GuestHotelBookingRedirect',
      label: 'hotelSearch',
      extraInfo: {
        name,
        joyPlaceId,
        hotelName,
        destinationURL: destinationURL || '',
        destinationDomain: destinationDomain || '',
        destinationName: destinationName || '',
        from,
        isSponsored,
        isDirectBooking,
        chain,
        isCoupleFavourite,
        utm_source,
        utm_medium,
        utm_campaign,
        utm_content,
        featuredPlacementVariant,
        scenario,
        placementPosition,
        affiliateHandoff,
        advertiserDomain,
        otaNightlyBaseRate,
        otaTotalRate,
        otaCurrency,
        otaPriceSource,
      },
    }),
    /**
     * A sponsored listing came near the viewport.
     *
     * Carries the same arm / surface / position fields as the click events on purpose: this is the
     * DENOMINATOR. Without them there is no per-arm click-through rate — only raw click counts,
     * which say nothing while the arms show a different number of sponsored hotels (control 0,
     * hero 1, row up to 4).
     */
    promotedHotelImpression: ({
      joyPlaceId,
      hotelName,
      chain,
      featuredPlacementVariant,
      scenario,
      placementPosition,
    }: {
      joyPlaceId: string;
      hotelName: string;
      chain?: string | null;
    } & FeaturedPlacementTelemetryFields) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'promotedHotelImpression',
        joyPlaceId,
        hotelName,
        chain,
        from: 'Accommodations Console',
        featuredPlacementVariant,
        scenario,
        placementPosition,
      },
    }),
    pickedByCoupleToggled: ({ state }: { state: boolean }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'pickedByCoupleToggled',
        state,
        from: 'Accommodations Console',
      },
    }),
    joyTravelBenefitsFilterChanged: ({ action, popular }: { action: 'added' | 'removed'; popular?: boolean }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'joyTravelBenefitsFilterChanged',
        action,
        from: 'Accommodations Console',
        ...(popular && { popular }),
      },
    }),
    freeCancellationFilterChanged: ({ action, popular }: { action: 'added' | 'removed'; popular?: boolean }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'freeCancellationFilterChanged',
        action,
        from: 'Accommodations Console',
        ...(popular && { popular }),
      },
    }),
    showAvailableOnlyFilterChanged: ({ action }: { action: 'added' | 'removed' }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'showAvailableOnlyFilterChanged',
        action,
        from: 'Accommodations Console',
      },
    }),
    // Cash-fund variant: "Save more, travel further" toggle (show only fund-covered stays). TRAV-3348.
    cashFundCoveredFilterChanged: ({ action }: { action: 'added' | 'removed' }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'cashFundCoveredFilterChanged',
        action,
        from: 'Accommodations Console',
      },
    }),
    // Cash-fund variant: dismissing the "Your Honeymoon Fund Goes Further" banner. TRAV-3348.
    cashFundBannerDismissed: () => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'cashFundBannerDismissed',
        from: 'Accommodations Console',
      },
    }),
    guestRatingFilterChanged: ({ rating, action, popular }: { rating: number; action: 'added' | 'removed'; popular?: boolean }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'guestRatingFilterChanged',
        rating,
        action,
        from: 'Accommodations Console',
        ...(popular && { popular }),
      },
    }),
    starRatingFilterChanged: ({ rating, action }: { rating: number; action: 'added' | 'removed' }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'starRatingFilterChanged',
        rating,
        action,
        from: 'Accommodations Console',
      },
    }),
    facilityFilterChanged: ({ facility, action, popular }: { facility: string; action: 'added' | 'removed'; popular?: boolean }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'facilityFilterChanged',
        facility,
        action,
        from: 'Accommodations Console',
        ...(popular && { popular }),
      },
    }),
    propertyCategoryFilterChanged: ({ propertyCategory, action, popular }: { propertyCategory: string; action: 'added' | 'removed'; popular?: boolean }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'propertyCategoryFilterChanged',
        propertyCategory,
        action,
        from: 'Accommodations Console',
        ...(popular && { popular }),
      },
    }),
    brandFilterChanged: ({ brand, action }: { brand: string; action: 'added' | 'removed' }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'brandFilterChanged',
        brand,
        action,
        from: 'Accommodations Console',
      },
    }),
    priceRangeFilterChanged: ({ minPrice, maxPrice }: { minPrice: number; maxPrice: number }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'priceRangeFilterChanged',
        minPrice,
        maxPrice,
        from: 'Accommodations Console',
      },
    }),
    allFiltersReset: () => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'allFiltersReset',
        from: 'Accommodations Console',
      },
    }),
    propertyNameSearched: ({ keyword, resultCount }: { keyword: string; resultCount: number }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'propertyNameSearched',
        keyword,
        resultCount,
        from: 'Accommodations Console',
      },
    }),
    sortOptionChanged: ({ sortOption }: { sortOption: SortOption }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'sortOptionChanged',
        sortOption,
        from: 'Accommodations Console',
      },
    }),
    /**
     * Fires once per hotel search, when the realtime pricing refresh settles.
     *
     * The three `sponsored*` / `featuredPlacement*` fields are additive (TRAV, featured-placement
     * A/B test) — no existing field changed name or meaning. They measure paid-placement FILL RATE:
     * `sponsoredSlotsRequested` is the `maxSponsoredSlots` the query sent, `sponsoredResultCount` is
     * how many returned properties were actually sponsored. The request is a ceiling capped
     * server-side by the contracted slot count, so the `row` arm can ask for 5 and render 2 with
     * nothing else on the page revealing it.
     */
    destinationSearched: ({
      googlePlaceId,
      locationName,
      responseTimeMs,
      resultCount,
      sponsoredSlotsRequested,
      sponsoredResultCount,
      featuredPlacementVariant,
      scenario,
      msToFirstLivePrice,
      livePricingPath,
    }: {
      googlePlaceId?: Maybe<string>;
      locationName?: Maybe<string>;
      /** Search start → every live price on screen. Same origin in both live-pricing paths. */
      responseTimeMs: number;
      /**
       * Search start → the first live price on screen — the figure batched pricing exists to improve.
       * Equal to `responseTimeMs` on the refetch path, where every price lands at once.
       */
      msToFirstLivePrice?: number;
      /**
       * Which path actually priced this search. Not the experiment arm: a `treatment` viewer still
       * takes `refetch` on the cash-fund console or when no pricing payload can be built.
       */
      livePricingPath?: 'batched' | 'refetch';
      resultCount: number;
      /**
       * The `maxSponsoredSlots` sent with the search. `0` means "we asked for none" (the `control`
       * arm); `undefined` means the field was OMITTED and the server's contracted count applied,
       * which is every surface outside the experiment. The two are not interchangeable.
       */
      sponsoredSlotsRequested?: number;
      /** How many of the returned properties came back with `isSponsored === true`. */
      sponsoredResultCount?: number;
      /** Undefined for the scenarios outside the featured-placement experiment. */
      featuredPlacementVariant?: FeaturedPlacementVariant;
      /**
       * Which console surface searched. Load-bearing for the fill-rate read: `guest` and `admin` are
       * both in the experiment, but only `guest` renders the featured unit, so pooling them makes an
       * inventory change look like a treatment effect.
       */
      scenario?: AccommodationsScenario;
    }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'destinationSearched',
        googlePlaceId,
        locationName,
        responseTimeMs,
        resultCount,
        sponsoredSlotsRequested,
        sponsoredResultCount,
        featuredPlacementVariant,
        scenario,
        msToFirstLivePrice,
        livePricingPath,
        from: 'Accommodations Console',
      },
    }),
    /**
     * Readout for the batched-live-pricing experiment. `msToFirstBatch` is the metric the change is
     * actually for — how soon the first rendered page has live prices — while `msToAllBatches` is the
     * total, which is expected to be no better than the single refetch it replaces.
     */
    livePricingSettled: ({
      batchCount,
      batchSize,
      joyPlaceIdCount,
      pricedCount,
      failedBatchCount,
      msToFirstBatch,
      msToAllBatches,
    }: {
      batchCount: number;
      batchSize: number;
      joyPlaceIdCount: number;
      pricedCount: number;
      failedBatchCount: number;
      msToFirstBatch: Maybe<number>;
      msToAllBatches: Maybe<number>;
    }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'livePricingSettled',
        batchCount,
        batchSize,
        joyPlaceIdCount,
        pricedCount,
        failedBatchCount,
        msToFirstBatch,
        msToAllBatches,
        from: 'Accommodations Console',
      },
    }),
  },
});

const { TelemetryProvider, useTelemetry } = createTelemetry(listingsPanelTelemetry, { eventId: '', section: '' });

export { TelemetryProvider, useTelemetry as useListingsPanelTelemetry };
